import { BaseApiService } from './BaseApiService';
import { logger } from '../utils/logger';
import { env } from '../config/environment';
import type { StockQuote, SearchResult, ChartData, ChartDataPoint } from '../types/market';

interface TradierQuoteResponse {
  quotes: {
    quote: Array<{
      symbol: string;
      last: number;
      change: number;
      change_percentage: number;
      volume: number;
      last_volume: number;
      trade_date: number;
    }>;
  };
}

interface TradierSearchResponse {
  securities: {
    security: Array<{
      symbol: string;
      description: string;
      type: string;
      exchange: string;
    }>;
  };
}

interface TradierHistoryResponse {
  history: {
    day: Array<{
      date: string;
      open: number;
      high: number;
      low: number;
      close: number;
      volume: number;
    }>;
  };
}

interface TradierIntradayResponse {
  series?: {
    data?: Array<{
      time: string;
      timestamp?: number;
      price: number;
      open?: number;
      high?: number;
      low?: number;
      close?: number;
      volume?: number;
    }>;
  };
}

export class TradierService extends BaseApiService {
  constructor() {
    if (!env.hasApiKey('tradier')) {
      throw new Error('VITE_TRADIER_API_KEY environment variable is required');
    }

    super('https://api.tradier.com/v1', {
      'Authorization': `Bearer ${env.apis.tradier}`,
      'Accept': 'application/json',
    });
  }

  async getQuotes(symbols: string[]): Promise<StockQuote[]> {
    try {
      const symbolsParam = symbols.join(',');
      const response = await this.get<TradierQuoteResponse>(
        `/markets/quotes?symbols=${symbolsParam}`
      );

      if (!response.quotes?.quote) {
        return [];
      }

      const quotes = Array.isArray(response.quotes.quote)
        ? response.quotes.quote
        : [response.quotes.quote];

      return quotes.map(quote => ({
        symbol: quote.symbol,
        price: quote.last || 0,
        change: quote.change || 0,
        change_percent: quote.change_percentage || 0,
        volume: quote.volume || quote.last_volume || 0,
        last_updated: new Date(quote.trade_date * 1000).toISOString(),
      }));
    } catch (error) {
      logger.error('Error fetching quotes from Tradier', error);
      throw error;
    }
  }



  async getIntradayData(symbol: string): Promise<ChartData> {
    try {
      // Get current date for today's intraday data
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      console.log(`TradierService: Requesting intraday data for ${symbol} on ${dateStr}`);
      
      // Try the basic timesales endpoint first (without time range)
      const response = await this.get<TradierIntradayResponse>(
        `/markets/timesales?symbol=${symbol}&interval=1min`
      );

      if (!response.series?.data || response.series.data.length === 0) {
        console.log('No intraday data received, falling back to daily data');
        // Fallback to daily data if intraday fails
        return this.getDailyDataForToday(symbol);
      }

      // Log the raw data to understand what we're getting
      console.log(`TradierService: Raw intraday data sample:`, response.series.data.slice(0, 3));

      // Process and filter intraday data for regular trading hours only
      const allData: ChartDataPoint[] = response.series.data
        .map(point => ({
          timestamp: point.time,
          open: point.open || point.price,
          high: point.high || point.price,
          low: point.low || point.price,
          close: point.close || point.price,
          volume: point.volume || 0,
        }))
        .filter(point => {
          // Basic validation - ensure we have valid data
          return point.close > 0 && point.timestamp;
        });

      // Temporarily show all data to debug the time issue
      const data: ChartDataPoint[] = allData;
      
      // Log sample timestamps to understand what times we're actually getting
      if (data.length > 0) {
        console.log(`TradierService: Sample timestamps from intraday data:`);
        data.slice(0, 5).forEach((point, index) => {
          const time = new Date(point.timestamp);
          console.log(`  ${index + 1}. ${point.timestamp} -> ${time.toString()} (${time.toLocaleTimeString()})`);
        });
        
        if (data.length > 5) {
          console.log(`  ... and ${data.length - 5} more data points`);
          const lastPoint = data[data.length - 1];
          const lastTime = new Date(lastPoint.timestamp);
          console.log(`  Last: ${lastPoint.timestamp} -> ${lastTime.toString()} (${lastTime.toLocaleTimeString()})`);
        }
      }

      console.log(`TradierService: Using ${data.length} intraday data points for ${symbol} (no filtering applied for debugging)`);

      return {
        symbol,
        data: data.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      };
    } catch (error) {
      logger.error('Error fetching intraday data from Tradier', error);
      console.log('Intraday API failed, falling back to recent daily data for 1D view');
      // Fallback to recent daily data (last few days)
      return this.getRecentDailyData(symbol);
    }
  }

  async getDailyDataForToday(symbol: string): Promise<ChartData> {
    try {
      const today = new Date();
      const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
      const formatDate = (date: Date) => date.toISOString().split('T')[0];
      
      const response = await this.get<TradierHistoryResponse>(
        `/markets/history?symbol=${symbol}&start=${formatDate(yesterday)}&end=${formatDate(today)}`
      );

      if (!response.history?.day) {
        return { symbol, data: [] };
      }

      const days = Array.isArray(response.history.day) 
        ? response.history.day 
        : [response.history.day];

      const data: ChartDataPoint[] = days.map(day => ({
        timestamp: day.date,
        open: day.open,
        high: day.high,
        low: day.low,
        close: day.close,
        volume: day.volume,
      }));

      return { symbol, data };
    } catch (error) {
      logger.error('Error fetching daily fallback data', error);
      return { symbol, data: [] };
    }
  }

  async getRecentDailyData(symbol: string): Promise<ChartData> {
    try {
      const today = new Date();
      const fiveDaysAgo = new Date(today.getTime() - 5 * 24 * 60 * 60 * 1000);
      const formatDate = (date: Date) => date.toISOString().split('T')[0];
      
      console.log(`TradierService: Fetching recent daily data for ${symbol} (fallback for 1D)`);
      
      const response = await this.get<TradierHistoryResponse>(
        `/markets/history?symbol=${symbol}&start=${formatDate(fiveDaysAgo)}&end=${formatDate(today)}`
      );

      if (!response.history?.day) {
        return { symbol, data: [] };
      }

      const days = Array.isArray(response.history.day) 
        ? response.history.day 
        : [response.history.day];

      const data: ChartDataPoint[] = days.map(day => ({
        timestamp: day.date,
        open: day.open,
        high: day.high,
        low: day.low,
        close: day.close,
        volume: day.volume,
      }));

      console.log(`TradierService: Using ${data.length} recent daily data points as 1D fallback`);

      return { 
        symbol, 
        data: data.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      };
    } catch (error) {
      logger.error('Error fetching recent daily fallback data', error);
      return { symbol, data: [] };
    }
  }

  async getHistoricalData(symbol: string, period: string = '1M'): Promise<ChartData> {
    try {
      // For 1D, use intraday data
      if (period === '1D') {
        return this.getIntradayData(symbol);
      }

      // Calculate dates based on period
      const now = new Date();
      const formatDate = (date: Date) => date.toISOString().split('T')[0]; // YYYY-MM-DD format

      let startDate: Date;

      switch (period) {
        case '5D':
          startDate = new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000); // 10 days to ensure 5 trading days
          break;
        case '1M':
          startDate = new Date(now.getTime() - 35 * 24 * 60 * 60 * 1000); // 35 days to ensure ~22 trading days
          break;
        case '1Y':
          startDate = new Date(now.getTime() - 370 * 24 * 60 * 60 * 1000); // Slightly more than 1 year
          break;
        case '5Y':
          startDate = new Date(now.getTime() - 5.2 * 365 * 24 * 60 * 60 * 1000); // Slightly more than 5 years
          break;
        case 'MAX':
        case 'Max':
          // For MAX, try to get as much data as possible - start from 20 years ago
          // But also try without start date to get maximum available data
          startDate = new Date(now.getTime() - 20 * 365 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = new Date(now.getTime() - 35 * 24 * 60 * 60 * 1000);
      }

      let url: string;
      if (period === 'MAX' || period === 'Max') {
        // For MAX, use a very old start date to get maximum available data
        const veryOldDate = new Date('1990-01-01'); // Start from 1990
        url = `/markets/history?symbol=${symbol}&start=${formatDate(veryOldDate)}&end=${formatDate(now)}`;
        console.log(`TradierService: Fetching MAX history data from ${formatDate(veryOldDate)} to ${formatDate(now)}`, url);
      } else {
        url = `/markets/history?symbol=${symbol}&start=${formatDate(startDate)}&end=${formatDate(now)}`;
        console.log(`TradierService: Fetching ${period} history data from ${formatDate(startDate)} to ${formatDate(now)}`, url);
      }

      const response = await this.get<TradierHistoryResponse>(url);

      if (!response.history?.day) {
        console.log(`No historical data received for ${symbol} - ${period}`);
        
        // For MAX, try alternative approach if first attempt fails
        if (period === 'MAX' || period === 'Max') {
          console.log(`TradierService: Trying alternative MAX approach without date parameters`);
          const altUrl = `/markets/history?symbol=${symbol}`;
          const altResponse = await this.get<TradierHistoryResponse>(altUrl);
          
          if (!altResponse.history?.day) {
            console.log(`TradierService: Alternative MAX approach also failed`);
            return { symbol, data: [] };
          }
          
          const altDays = Array.isArray(altResponse.history.day)
            ? altResponse.history.day
            : [altResponse.history.day];

          const altData: ChartDataPoint[] = altDays.map(day => ({
            timestamp: day.date,
            open: day.open,
            high: day.high,
            low: day.low,
            close: day.close,
            volume: day.volume,
          }));

          console.log(`TradierService: Alternative MAX returned ${altData.length} data points`);
          
          return {
            symbol,
            data: altData.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
          };
        }
        
        return {
          symbol,
          data: []
        };
      }

      const days = Array.isArray(response.history.day)
        ? response.history.day
        : [response.history.day];

      const data: ChartDataPoint[] = days.map(day => ({
        timestamp: day.date,
        open: day.open,
        high: day.high,
        low: day.low,
        close: day.close,
        volume: day.volume,
      }));

      console.log(`TradierService: Received ${data.length} historical data points for ${symbol} - ${period}`);
      
      // Add debugging for MAX range to see date range of returned data
      if ((period === 'MAX' || period === 'Max') && data.length > 0) {
        const firstDate = new Date(data[0].timestamp);
        const lastDate = new Date(data[data.length - 1].timestamp);
        console.log(`TradierService: MAX data range: ${firstDate.toDateString()} to ${lastDate.toDateString()}`);
        console.log(`TradierService: MAX data spans ${Math.round((lastDate.getTime() - firstDate.getTime()) / (365 * 24 * 60 * 60 * 1000))} years`);
      }

      return {
        symbol,
        data: data.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      };
    } catch (error) {
      logger.error('Error fetching historical data from Tradier', error);
      throw error;
    }
  }

  async searchStocks(query: string): Promise<SearchResult[]> {
    try {
      if (!query || query.length < 1) {
        return [];
      }

      const response = await this.get<TradierSearchResponse>(
        `/markets/search?q=${encodeURIComponent(query)}&indexes=false`
      );

      if (!response.securities?.security) {
        return [];
      }

      const securities = Array.isArray(response.securities.security)
        ? response.securities.security
        : [response.securities.security];

      return securities
        .filter(security => security.type === 'stock')
        .slice(0, 10) // Limit to 10 results
        .map(security => ({
          symbol: security.symbol,
          name: security.description,
          type: security.type,
          exchange: security.exchange,
        }));
    } catch (error) {
      logger.error('Error searching stocks with Tradier', error);
      throw error;
    }
  }



  async getMarketStatus(): Promise<{ status: string; next_state_change: string }> {
    try {
      const response = await this.get<any>('/markets/clock');
      return {
        status: response.clock?.state || 'unknown',
        next_state_change: response.clock?.next_state_change || '',
      };
    } catch (error) {
      logger.error('Error fetching market status from Tradier', error);
      throw error;
    }
  }
}